import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { aaiApi } from '../../services/aaiApi';
import { DimensionRanges, ErrorNote, Loading, StagePill } from './components';
import { useAsync } from './useAsync';

const MyReadinessTab: React.FC<{ onStartOnboarding: () => void }> = ({ onStartOnboarding }) => {
  const { data, loading, error } = useAsync(() => aaiApi.getMyReadiness(), 'Could not load your readiness.');

  if (loading) return <Loading label="Loading your readiness…" />;
  if (error) return <ErrorNote message={error} />;

  // Not onboarded yet → growth-framed call to action (never a leaderboard).
  if (!data?.readiness) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-100 p-6 text-center">
        <Sparkles size={36} className="text-brand-500 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-gray-900">Let's map where you are with AI</h2>
        <p className="text-sm text-gray-600 mt-1 mb-4">
          A quick ~5-minute onboarding builds your private readiness profile so your coach can meet
          you where you are. It's about your growth — never a ranking.
        </p>
        <Button variant="brand" onClick={onStartOnboarding}>
          Start onboarding <ArrowRight size={15} className="ml-1.5" />
        </Button>
      </div>
    );
  }

  const r = data.readiness;
  const briefFields = data.brief?.fields ? Object.values(data.brief.fields) : [];

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
          <h2 className="text-lg font-semibold text-gray-900">Your AI readiness</h2>
          <StagePill stage={r.stage} />
        </div>
        <p className="text-sm text-gray-500 mb-5">
          Shown as ranges, not exact scores — this reflects where you are today, privately.
          {r.measured_at && <> Last updated {new Date(r.measured_at).toLocaleDateString()}.</>}
        </p>
        <DimensionRanges dimensions={r.dimensions} />
      </div>

      {briefFields.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-3">How you work</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {briefFields.map((f) => (
              <div key={f.label} className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                <p className="text-xs text-gray-500">{f.label}</p>
                <p className="text-sm text-gray-800 mt-0.5">
                  {Array.isArray(f.value) ? (f.value as unknown[]).join(', ') : String(f.value)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyReadinessTab;
